import React, { Fragment, useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import api from '../../services/api';

const DEFAULT_TITLE = 'Disclaimer';

// ── Trimmed: removed sentences that repeated the "verify before purchase" and
//    "not liable" themes that appeared both in the intro and bullet list. ──
const DEFAULT_HTML = `
<p>Search results are a <em>guide only</em>. Parts may vary due to model variations, options, or aftermarket modifications.</p>
<p>• Verify the correct part number and fitment <strong>before purchase</strong>. Consult your installer if unsure.</p>
<p>• Inspect glass before fitting — defects must be reported <strong>prior to installation</strong>.</p>
<p>• <strong>Auto Glass Shop</strong> is not liable for incorrect selection, installation issues, or consequential losses.</p>
<p>By continuing you confirm you have read and accept these terms.</p>
`;

const REDIRECT = 'https://www.google.com.au';

export default function DisclaimerModal({ open, onClose, onAgree }) {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState({ title: DEFAULT_TITLE, html: DEFAULT_HTML });

  useEffect(() => {
    let mounted = true;
    const fetchCMS = async () => {
      try {
        setLoading(true);
        let res = null;
        try { res = await api.get(`/cms-pages/slug/${encodeURIComponent('disclaimer')}`); } catch (_) {}
        console.log('[DisclaimerModal] CMS response:', res?.data);
        console.log('[DisclaimerModal] DEFAULT_HTML starts with:', DEFAULT_HTML.substring(0, 60));
        if (mounted && res?.data) {
          // DEBUG: always use DEFAULT_HTML to rule out CMS caching
          setContent({
            title: res.data.title || DEFAULT_TITLE,
            html: DEFAULT_HTML,
          });
        } else if (mounted) {
          setContent({ title: DEFAULT_TITLE, html: DEFAULT_HTML });
        }
      } catch (_) {
        if (mounted) setContent({ title: DEFAULT_TITLE, html: DEFAULT_HTML });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (open) fetchCMS();
    return () => { mounted = false; };
  }, [open]);

  return (
    <Transition show={open} as={Fragment}>
      {/* Full-screen overlay — centered in viewport */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">

        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => window.location.assign(REDIRECT)}
        />

        {/*
          Panel — centered in viewport, compact enough for iPhone SE
        */}
        <div
          className="relative bg-white rounded-xl shadow-2xl flex flex-col"
          style={{ width: 'min(480px, calc(100vw - 32px))', maxHeight: 'min(80svh, 80vh)' }}
        >
          {/* ── Header ── */}
          <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-gray-200 flex-shrink-0">
            {/* Drag handle (mobile feel) */}
            <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full bg-gray-300 sm:hidden" />
            <h2 className="text-xs font-bold tracking-wide uppercase text-gray-800">
              {content.title}
            </h2>
            {/* Shield icon */}
            <svg className="w-4 h-4 text-amber-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 00-2 0v3a1 1 0 002 0V7z" clipRule="evenodd" />
            </svg>
          </div>

          {/* ── Scrollable content ── */}
          <div className="overflow-y-auto flex-1 px-4 py-2">
            {loading ? (
              <p style={{ fontSize: 10 }} className="text-gray-400 text-center py-2">Loading…</p>
            ) : (
              <div
                style={{ fontSize: 10, lineHeight: 1.5 }}
                className="
                  text-gray-700
                  [&_p]:mt-0 [&_p]:mb-1.5 [&_p:last-child]:mb-0
                  [&_p]:pl-3 [&_p]:-indent-3
                  [&_strong]:font-semibold [&_em]:italic
                "
                dangerouslySetInnerHTML={{ __html: content.html }}
              />
            )}
          </div>

          {/* ── Buttons — always visible, never scroll away ── */}
          <div className="px-4 py-3 border-t border-gray-200 flex-shrink-0 flex gap-2">
            <button
              type="button"
              onClick={() => window.location.assign(REDIRECT)}
              style={{ fontSize: 11 }}
              className="
                flex-1 py-2 rounded-lg border border-gray-300
                text-gray-700 font-medium
                active:bg-gray-100 hover:bg-gray-50
                transition-colors
              "
            >
              No, Exit
            </button>
            <button
              type="button"
              onClick={onAgree}
              style={{ fontSize: 11 }}
              className="
                flex-1 py-2 rounded-lg
                bg-primary-600 text-white font-semibold
                active:bg-primary-800 hover:bg-primary-700
                transition-colors
              "
            >
              Yes, I Agree
            </button>
          </div>
        </div>
      </div>
    </Transition>
  );
}
